import type { ClassValue } from "clsx";
import { clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import {
  differenceInDays,
  differenceInMonths,
  differenceInYears,
} from "date-fns";
import { openDB } from "idb";
import { toast } from "sonner";

const COMMON_UNITS = ["small", "medium", "large"];
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      opacity: ["disabled"],
      spacing: ["divider"],
      borderWidth: COMMON_UNITS,
      borderRadius: COMMON_UNITS,
    },
    classGroups: {
      shadow: [{ shadow: COMMON_UNITS }],
      "font-size": [{ text: ["tiny", ...COMMON_UNITS] }],
      "bg-image": ["bg-stripe-gradient"],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function calculateAge(birthDate: Date) {
  const currentDate = new Date(); // current date
  // @ts-ignore

  // const age = intervalToDuration({
  //   start: birthDate,
  //   end: currentDate,
  // });
  const ageInYears = differenceInYears(currentDate, birthDate);
  const ageINMonths = differenceInMonths(currentDate, birthDate);
  const ageInDays = differenceInDays(currentDate, birthDate);

  return {
    years: ageInYears,
    months: ageINMonths,
    days: ageInDays,
  };
}

interface generateIdProps {
  farmName: string;
  farmID: string;
  animalGN: string;
}

export const generateId = ({ farmName, farmID, animalGN }: generateIdProps) => {
  const formatPart = (part: any) => {
    return part.toString().padStart(4, "0").slice(0, 4);
  };

  const farmPart = formatPart(farmName);
  const farmIDPart = formatPart(farmID);
  const animalGNPart = formatPart(animalGN);

  return `${farmPart}-${farmIDPart}-${animalGNPart}`;
};

const dbPromise = openDB("posts-store", 1, {
  upgrade(db) {
    if (!db.objectStoreNames.contains("posts")) {
      db.createObjectStore("posts", { keyPath: "_id" });
    }

    if (!db.objectStoreNames.contains("sync-posts")) {
      db.createObjectStore("sync-posts", { keyPath: "id" });
    }
  },
});

export const writeData = async (st: string, data: any) => {
  try {
    dbPromise.then(function (db) {
      const tx = db.transaction(st, "readwrite");
      const store = tx.objectStore(st);

      store.put(data); // Store the object

      return tx.done;
    });
  } catch (error: unknown) {
    // console.log(error);
  }
};

export const readAllData = async (storeName: string) => {
  return dbPromise.then(function (db) {
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);

    return store.getAll();
  });
};

export const clearAllData = async (st: any) => {
  return dbPromise.then(function (db) {
    const tx = db.transaction(st, "readwrite");
    const store = tx.objectStore(st);

    store.clear();

    return tx.done;
  });
};

export const deleteItemFromData = (st: any, id: any) => {
  dbPromise
    .then(function (db) {
      const tx = db.transaction(st, "readwrite");
      const store = tx.objectStore(st);

      store.delete(id);

      return tx.done;
    })
    .then(function () {
      // console.log("Item Deleted");
    });
};

type sync = {
  storeName: string;
  eventTag: string;
  // isSuccess: boolean;
  // status: string;
  // form: any;
  mutateAsync: any;
  data: any;
};

export const backgroundSync = async ({
  mutateAsync,
  storeName,
  eventTag,
  data,
}: sync) => {
  if (navigator.onLine) {
    await mutateAsync(data);
  }
  const storedAuth = localStorage.getItem("auth");

  if (storedAuth !== null) {
    const auth = JSON.parse(storedAuth);

    if ("serviceWorker" in navigator && "SyncManager" in window) {
      navigator.serviceWorker.ready.then(function (sw) {
        writeData(storeName, {
          url: "/",
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.state.userInfo.token}`,
            "Content-Type": "application/json",
          },
          body: data,
          timestamp: Date.now(),
        })
          .then(function () {
            //@ts-ignore
            return sw.sync.register(eventTag);
          })
          .then(function () {
            toast.message("Successfully sync the post");
          })
          .catch(function () {
            // console.log(err);
          });
      });
    }
  }
};
export const theme = {
  colors: {
    primary: "#3498db",
    secondary: "#2ecc71",
    background: "#f8f9fa",
    text: "#2c3e50",
    border: "#bdc3c7",
    error: "#e74c3c",
    success: "#27ae60",
  },
  shadows: {
    small: "0 2px 4px rgba(0,0,0,0.1)",
    medium: "0 4px 6px rgba(0,0,0,0.1)",
    large: "0 8px 16px rgba(0,0,0,0.1)",
  },
  transitions: {
    default: "all 0.3s ease",
  },
};

export function formatDate(date: Date | undefined) {
  if (!date) {
    return "";
  }
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}
